import { Bar } from "react-chartjs-2";
import React, { useMemo } from "react";
import {
  Align,
  Chart,
  ChartDataset,
  ChartOptions,
  registerables,
} from "chart.js";
import { SaturationChartProps } from "./types";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "./SaturationChart.scss";
import "../../../pages/dashboard/i18n";
import { useTranslation } from "react-i18next";
import { BasicIcon, ToolTipPosition, Tooltip } from "@pickit/pickit-components";
import {
  ASSIGNED_TRANSACTIONS_STATES,
  DATE_FORMATS,
  PENDING_ASSIGNMENT_STATES,
} from "utils/constants";
import moment from "moment";

Chart.register(...registerables, ChartDataLabels);

const SaturationChart = ({
  data,
  color,
  barHeight = 10,
  isSingleRegion,
  regions,
}: SaturationChartProps) => {
  const inflateAmount = 1.5;
  const { t } = useTranslation();
  const chartHeight = useMemo(
    () => (barHeight + inflateAmount * 2) * data.length * 3 + 100,
    [barHeight, data.length]
  );

  const stateFilters = [
    ASSIGNED_TRANSACTIONS_STATES,
    PENDING_ASSIGNMENT_STATES,
  ];
  
  const transactionUrlFilter = useMemo((): string => {
    const { shortISODate } = DATE_FORMATS;
    const today = moment().format(shortISODate);
    let filters = `minSla=${today}&maxSla=${today}`;
    return filters;
  }, []);

  const getBarOptions: (backgroundBar: boolean) => ChartOptions<"bar"> = (
    backgroundBar
  ) => ({
    indexAxis: "y",
    maintainAspectRatio: false,

    plugins: {
      legend: {
        display: false,
      },
      title: {
        text: t("dashboard:saturationTitle"),
        display: true,
        position: "top",
        padding: { bottom: 44, top: 10 },
        align: "start",
        color: backgroundBar ? "transparent" : "black",
        font: {
          size: 20,
          weight: 700,
          family: "Mulish, sans-serif",
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: "transparent",
          maxTicksLimit: 6,
        },
        stacked: true,
        max: 100,
      },
      y: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: { display: false },
        stacked: true,
      },
    },
  });

  const datasetConfig: ChartDataset<"bar"> = {
    data: [],
    barThickness: barHeight,
    borderSkipped: false,
    inflateAmount,
  };

  const alignments: { [key in "align" | "anchor"]: Align } = {
    anchor: "end",
    align: "start",
  };

  const getAlignmet = (index: number, type: "align" | "anchor"): Align => {
    return data[index].assigned / data[index].total < 0.03
      ? "center"
      : alignments[type];
  };

  return (
    <div className="saturation-chart">
      <div className="saturation-chart-tooltip">
        <Tooltip
          message={t("dashboard:saturationTooltip")}
          position={ToolTipPosition.top}
        >
          <BasicIcon
            name="information-circle"
            size="extra-small"
            color="#0098ff"
          />
        </Tooltip>
      </div>
      <div style={{ height: chartHeight }}>
        <Bar
          className="data-chart"
          height="100%"
          options={{
            ...getBarOptions(false),
            onClick: (evt, element, chart) => {
              if (element.length > 0) {
                const filters = `${transactionUrlFilter}&${
                  isSingleRegion ? `regions=${regions}&workZones` : "regions"
                }=${data[element[0].index].id}`;
                window.open(
                  `transaction?state=${stateFilters[
                    element[0].datasetIndex
                  ].join(",")}&${filters}`
                );
              }
            },
            animation: {
              duration: 0,
            },
            onHover: (e, activeElements, chart) => {
              chart.canvas.style.cursor = activeElements[0]
                ? "pointer"
                : "default";

              chart.update();
            },
            plugins: {
              ...getBarOptions(false).plugins,
              tooltip: { enabled: false },
            },
          }}
          data={{
            labels: data.map((d) => d.name),
            datasets: [
              {
                ...datasetConfig,
                data: data.map((d) =>
                  Math.max((d.assigned * 100) / d.total - 0.5, 0)
                ),
                label: "Asigandas",
                backgroundColor: color,
                borderRadius: [
                  {
                    bottomLeft: 1000,
                    bottomRight: 1000,
                    topLeft: 1000,
                    topRight: 1000,
                  },
                ],

                datalabels: {
                  labels: {
                    value: {
                      color: "white",
                      formatter: (_, context) =>
                        data[context.dataIndex].assigned > 0
                          ? data[context.dataIndex].assigned
                          : "",
                      anchor: (context) =>
                        getAlignmet(context.dataIndex, "anchor"),
                      align: (context) =>
                        getAlignmet(context.dataIndex, "align"),
                      font: {
                        size: 14,
                        weight: 700,
                        family: "Mulish, sans-serif",
                      },
                    },
                    title: {
                      color: "#2d2d2d",
                      formatter: (_, context) =>
                        context.chart.data.labels &&
                        context.chart.data.labels[context.dataIndex],
                      anchor: "start",
                      align: 312,
                      textAlign: "left",
                      offset: 5,
                      font: {
                        size: 14,
                        family: "Mulish, sans-serif",
                      },
                    },
                  },
                },
              },
              {
                ...datasetConfig,
                data: data.map((d) =>
                  d.total > 0 && d.assigned > 0
                    ? 100 - (d.assigned * 100) / d.total
                    : 99.5
                ),
                backgroundColor: (context) => data[context.dataIndex].total > 0 ? "#CEEBFF" : "#F5F5F5",
                label: "Sin asignar",
                borderRadius: [
                  {
                    bottomRight: 1000,
                    topRight: 1000,
                  },
                ],
                datalabels: {
                  labels: {
                    value: {
                      color: (context) => data[context.dataIndex].total > 0 ? "#0098ff" : "#b8b8b8",
                      formatter: (_, context) => data[context.dataIndex].total,
                      anchor: "end",
                      align: "start",
                      font: {
                        size: 14,
                        weight: 700,
                        family: "Mulish, sans-serif",
                      },
                    },
                    percentage: {
                      formatter: (_, context) =>
                        data[context.dataIndex].total > 0
                          ? `${(
                              (data[context.dataIndex].assigned * 100) /
                              data[context.dataIndex].total
                            ).toFixed(1)}%`
                          : "0%",
                      anchor: "end",
                      align: 235,
                      font: {
                        size: 14,
                        family: "Mulish, sans-serif",
                      },
                    },
                  },
                },
              },
            ],
          }}
        />
      </div>
    </div>
  );
};

export default SaturationChart;
